import classNames from "classnames";
import { useFormik } from "formik";
import _ from "lodash";
import moment from "moment";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTimezoneSelect } from "react-timezone-select";
import { toast } from "react-toastify";
import { Button } from "../../../components/buttons/button";
import { FileUploadButton } from "../../../components/buttons/fileUploadButton";
import { InfoCard } from "../../../components/cards/infoCard";
import ComboBox from "../../../components/dropdown/comboBox";
import { EventImage } from "../../../components/event/eventImage";
import { CaretDown } from "../../../components/icon/downcaret";
import { Icon } from "../../../components/icon/icon";
import { Checkbox } from "../../../components/inputs/checkbox";
import { DateInput } from "../../../components/inputs/dateInput";
import { SwitchInput } from "../../../components/inputs/switch";
import { TextInput } from "../../../components/inputs/textInput";
import { TimeInput } from "../../../components/inputs/timeInput";
import { LoadingOval } from "../../../components/loaders/loaders";
import { TabNav } from "../../../components/tabNav";
import { ErrorText } from "../../../components/typography/errorText";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useDebounceSearch } from "../../../customeHooks/useDebounce";
import { useFileUpload } from "../../../customeHooks/useFileUpload";
import { useGoogleMaps } from "../../../customeHooks/useGoogleMaps";
import {
  useEditEventMutation,
  useLazyGetEventQuery,
} from "../../../redux/features/event/event.api";
import { CreateEvent } from "../../../redux/features/event/event.types";
import {
  DEFAULT_EVENT_IMAGE_URL,
  eventCategories,
  MAX_SIZE_MB,
  PRIMARY_COLOR,
  tzOptions,
} from "../../../utils/constants";
import {
  formatDate,
  formatTime,
  getEventUrl,
  parseDateStringToDate,
  parseTimeToDate,
} from "../../../utils/helpers";
import { EventDetailsSchema } from "../../../utils/validations";

export const EventDetails = ({
  finalPayload,
  currentPayload,
  setFinalPayload,
  setCurrentPayload,
  setStep,
  mode,
  id = "",
  ticketsBought = false,
  handleModalOpen,
}: {
  finalPayload: CreateEvent;
  currentPayload: CreateEvent;
  setFinalPayload: React.Dispatch<React.SetStateAction<CreateEvent>>;
  setCurrentPayload: React.Dispatch<React.SetStateAction<CreateEvent>>;
  setStep: (step: number) => void;
  mode: "create" | "edit";
  id?: string;
  ticketsBought?: boolean;
  handleModalOpen: (name: string) => void;
}) => {
  const navigate = useNavigate();

  const { options, parseTimezone } = useTimezoneSelect(tzOptions);

  const {
    uploading,
    handleSingleFileUpload,
    error: imageUploadError,
    uploadedFile,
  } = useFileUpload();

  const {
    data: locations,
    isLoaded,
    loading: loadingLocations,
    handleSelect: handleLocationSelection,
    setAutoCompleteValue,
  } = useGoogleMaps();

  const [editEvent, { isLoading: editingEvent }] = useEditEventMutation();

  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    setFieldValue,
    validateField,
  } = useFormik({
    initialValues: {
      name: currentPayload.name,
      description: currentPayload.description,
      category: currentPayload.category,
      timezone: currentPayload.timezone,
      hideLocation: currentPayload.hideLocation,
      placeId: currentPayload.placeId,
      placeholderLocation: currentPayload.placeholderLocation,
      longitude: currentPayload.longitude,
      latitude: currentPayload.latitude,
      location: currentPayload.location,
      startDate: currentPayload.startDate,
      endDate: currentPayload.endDate,
      startTime: currentPayload.startTime,
      endTime: currentPayload.endTime,
      image: currentPayload.image,
      type: currentPayload.type,
      status: currentPayload.status,
      slug: currentPayload.slug,
      customSlug: ![null, undefined, ""].includes(currentPayload.slug),
    },
    enableReinitialize: true,
    validationSchema: EventDetailsSchema,
    onSubmit: async () => {
      // If creating or editing a draft event
      if (
        mode === "create" ||
        (mode === "edit" && finalPayload.status === "draft")
      ) {
        const { customSlug, ...otherValues } = values;

        const payloadData = {
          ...finalPayload,
          ...otherValues,
          image: values.image === "" ? DEFAULT_EVENT_IMAGE_URL : values.image,
          startDate: otherValues.startDate,
          endDate: otherValues.endDate,
          latitude: otherValues.latitude,
          longitude: otherValues.longitude,
          placeId: otherValues.placeId,
          placeholderLocation: otherValues.placeholderLocation || undefined,
        };

        setCurrentPayload(payloadData);

        setStep(2);
      }
      // If editing any other event
      else if (mode === "edit") {
        const resp = await editEvent({
          id: id,
          payload: {
            ...finalPayload,
            tickets: undefined,
          },
        });

        if (resp.data?.status === "success") {
          toast.success("Event updated successfully");
          navigate(
            `/events/${encodeURIComponent(resp.data.data.event.slug)}/details`,
            {
              replace: true,
            },
          );
        }
      }
    },
  });

  const { debouncedQuery: debouncedSlug } = useDebounceSearch({
    searchQuery: values.slug,
  });

  const [
    validateSlug,
    { isSuccess: slugFetchSuccess, isFetching: fetchingSlug },
  ] = useLazyGetEventQuery();

  useEffect(() => {
    const { customSlug, ...otherValues } = values;

    const payloadData = {
      ...finalPayload,
      ...otherValues,
      image: otherValues.image,
      startDate: otherValues.startDate,
      endDate: otherValues.endDate,
      latitude: otherValues.latitude,
      longitude: otherValues.longitude,
      placeId: otherValues.placeId,
      placeholderLocation: otherValues.placeholderLocation || undefined,
    };

    setFinalPayload(payloadData);
    // eslint-disable-next-line
  }, [values]);

  useEffect(() => {
    if (
      !debouncedSlug ||
      !values.customSlug ||
      debouncedSlug === currentPayload.slug ||
      !!errors.slug
    ) {
      return;
    } else {
      validateSlug({
        slug: debouncedSlug as string,
      });
    }
    // eslint-disable-next-line
  }, [debouncedSlug]);

  return (
    <>
      <div className="flex w-full flex-col gap-[2px]">
        <BgWrapper className="text-base font-bold text-grey-950">
          Event Details
        </BgWrapper>

        <BgWrapper className="">
          <TabNav
            navs={[
              {
                title: "Physical Event",
                to: "physical",
                onClick: (selected) => {
                  setFieldValue("location", "");
                  setFieldValue("hideLocation", false);
                  setFieldValue("placeholderLocation", undefined);
                  setFieldValue("type", selected);
                },
              },
              {
                title: "Virtual Event",
                to: "virtual",
                onClick: (selected) => {
                  setFieldValue("location", "");
                  setFieldValue("hideLocation", false);
                  setFieldValue("placeholderLocation", undefined);
                  setFieldValue("type", selected);
                },
              },
            ]}
            activeTab={values.type}
            variant="switcher"
            tabClassName=""
            className="col-span-full"
          />

          <form className="mt-4 grid grid-cols-2 items-start gap-4">
            <TextInput
              type="text"
              name="name"
              value={values.name}
              placeholder="Enter event name"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="Event Name"
              errors={errors.name && touched.name ? errors.name : ""}
              autoFocus
              containerClassName="col-span-full order-first"
            />

            <ComboBox
              menuItems={eventCategories.map((cat) => {
                return {
                  name: cat.name,
                  value: cat.value,
                };
              })}
              onSelectOption={(selected) => {
                setFieldValue("category", selected);
                console.log(selected);
              }}
              menuClassName="!max-h-[200px] translate-x-[-12px]"
              position="bottom start"
              immediate
              type="text"
              name="category"
              value={values.category}
              placeholder={"Select category"}
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title={"Category"}
              errors={
                errors.category && touched.category ? errors.category : ""
              }
              readOnly
              containerClassName="col-span-full"
              customIcon={<CaretDown />}
              customIconPosition="right"
            />

            <ComboBox
              menuItems={options.map((opt) => {
                return {
                  name: opt.label,
                  value: opt.value,
                };
              })}
              onSelectOption={(selected) => {
                setFieldValue("timezone", selected);
              }}
              menuClassName="!max-h-[200px] translate-x-[-12px]"
              position="bottom start"
              immediate
              type="text"
              name="timezone"
              value={parseTimezone(values.timezone)?.label}
              placeholder={"Select time zone"}
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title={"Time Zone"}
              readOnly
              errors={
                errors.timezone && touched.timezone ? errors.timezone : ""
              }
              containerClassName="col-span-full"
              customIcon={<CaretDown />}
              customIconPosition="right"
            />

            <DateInput
              name="startDate"
              onBlur={handleBlur}
              placeholder="Select start date"
              errors={
                errors.startDate && touched.startDate ? errors.startDate : ""
              }
              title="Start Date"
              date={parseDateStringToDate(
                values.startDate,
                "MMMM DD, YYYY",
                true,
              )}
              setDate={async (date) => {
                setFieldValue("startDate", formatDate(date)).then(() => {
                  validateField("startDate");
                });

                if (
                  !values.endDate ||
                  (date &&
                    values.endDate &&
                    date >
                      parseDateStringToDate(
                        values.endDate,
                        "MMMM DD, YYYY",
                        true,
                      )!)
                ) {
                  setFieldValue("endDate", formatDate(date)).then(() => {
                    validateField("endDate");
                  });
                }
              }}
            />

            <TimeInput
              name="startTime"
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Select start time"
              errors={
                errors.startTime && touched.startTime ? errors.startTime : ""
              }
              title="Start Time"
              time={parseTimeToDate(values.startTime)}
              setDate={(time) => {
                setFieldValue("startTime", formatTime(time, "h:mm A"));
              }}
            />

            <DateInput
              name="endDate"
              onBlur={handleBlur}
              placeholder="Select end date"
              errors={errors.endDate && touched.endDate ? errors.endDate : ""}
              title="End Date"
              date={parseDateStringToDate(
                values.endDate,
                "MMMM DD, YYYY",
                true,
              )}
              minDate={
                values.startDate !== ""
                  ? moment(values.startDate, "MMMM DD, YYYY", true)
                  : undefined
              }
              setDate={(date) => {
                setFieldValue("endDate", formatDate(date));
              }}
            />

            <TimeInput
              name="endTime"
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Select end time"
              errors={errors.endTime && touched.endTime ? errors.endTime : ""}
              title="End Time"
              time={parseTimeToDate(values.endTime)}
              setDate={(time) => {
                setFieldValue("endTime", formatTime(time, "h:mm A"));
              }}
            />

            <div className="col-span-full flex flex-col gap-2">
              {values.type === "physical" ? (
                <ComboBox
                  menuItems={locations.map((loc) => {
                    return {
                      name: loc.description,
                      value: {
                        name: loc.description,
                        id: loc.place_id,
                      },
                    };
                  })}
                  className="col-span-full"
                  loading={!isLoaded || loadingLocations}
                  onSelectOption={(val: { name: string; id: string }) => {
                    handleLocationSelection(
                      val.name,
                      val.id,
                      true,
                      (locData) => {
                        setFieldValue("location", val.name);
                        setFieldValue(
                          "placeId",
                          locData?.destinationGooglePlaceID,
                        );
                        setFieldValue("longitude", String(locData?.lng));
                        setFieldValue("latitude", String(locData?.lat));
                        setFieldValue(
                          "placeholderLocation",
                          `${String(locData?.state)}, ${String(locData?.country)}`,
                        );
                      },
                    );
                  }}
                  onSearch={(search) => {
                    setFieldValue("placeId", undefined);
                    setFieldValue("longitude", undefined);
                    setFieldValue("latitude", undefined);
                    setAutoCompleteValue(search);
                  }}
                  immediate={false}
                  showEmptyState={false}
                  type="text"
                  name="location"
                  value={values.location}
                  placeholder={"Enter address"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title={"Address/Location"}
                  errors={
                    (errors.location ||
                      errors.placeId ||
                      errors.longitude ||
                      errors.latitude) &&
                    touched.location
                      ? errors.location ||
                        errors.placeId ||
                        errors.longitude ||
                        errors.latitude
                      : ""
                  }
                  containerClassName=""
                />
              ) : (
                <TextInput
                  type="text"
                  name="location"
                  value={values.location}
                  placeholder={"Enter url"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title={"Meeting/Call Link"}
                  errors={
                    errors.location && touched.location ? errors.location : ""
                  }
                  containerClassName="col-span-full"
                />
              )}

              <Checkbox
                checkStatus={values.hideLocation}
                handleChange={() => {
                  setFieldValue("hideLocation", !values.hideLocation);
                }}
                text={
                  <span className="font-normal text-grey-950">
                    {values.type === "physical"
                      ? `Hide address/location until guest get a ticket`
                      : `Hide Meeting / Call Link until guest get a ticket`}
                  </span>
                }
                textPosition="right"
                className="col-span-full text-left"
              />

              {values.hideLocation && (
                <TextInput
                  type="text"
                  name="placeholderLocation"
                  value={values.placeholderLocation}
                  placeholder={
                    values.type === "physical"
                      ? "Enter placeholder location"
                      : "Enter placeholder Meeting / Call Link"
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title={
                    values.type === "physical"
                      ? "Placeholder Location"
                      : "Placeholder Meeting / Call Link"
                  }
                  errors={
                    errors.placeholderLocation && touched.placeholderLocation
                      ? errors.placeholderLocation
                      : ""
                  }
                  containerClassName="col-span-full"
                />
              )}
            </div>

            <TextInput
              type="textarea"
              name="description"
              value={values.description}
              placeholder="Enter description"
              onChange={handleChange}
              onBlur={handleBlur}
              hasTitle
              title="Description"
              errors={
                errors.description && touched.description
                  ? errors.description
                  : ""
              }
              containerClassName={classNames("col-span-full", {
                "order-[-9998]": mode === "edit",
              })}
              rows={1}
            />

            {/* Custom URL  */}

            <div className="col-span-full flex w-full flex-col gap-3 border-t border-grey-100 pt-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <MiniHeader
                    header="Customise URL (Optional)"
                    subheader="Customise your event URL, no spaces or special characters allowed."
                  />
                </div>

                <SwitchInput
                  checked={values.customSlug}
                  onChange={() => {
                    if (values.customSlug) {
                      setFieldValue("slug", "");
                    }
                    setFieldValue("customSlug", !values.customSlug);
                  }}
                />
              </div>

              {values.customSlug && (
                <div className="flex items-start gap-1">
                  <TextInput
                    customIcon={
                      <Icon
                        svg="globe"
                        width={20}
                        height={20}
                        className="text-grey-500"
                      />
                    }
                    type="text"
                    value={getEventUrl("")}
                    readOnly
                    onChange={() => {}}
                    onBlur={() => {}}
                    className="!bg-white"
                    containerClassName="w-full max-w-[189px]"
                    name="appDomain"
                    placeholder=""
                  />

                  <TextInput
                    type="text"
                    value={values.slug ?? ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="w-full"
                    containerClassName="w-full"
                    name="slug"
                    placeholder="Enter custom URL"
                    hasTitle
                    title=""
                    customIcon={
                      fetchingSlug ? (
                        <LoadingOval
                          color={PRIMARY_COLOR}
                          loaderWidth="24"
                          loaderHeight="24"
                        />
                      ) : !slugFetchSuccess && values.slug && !errors.slug ? (
                        <Icon
                          className="text-success"
                          svg="circle-check"
                          width={20}
                          height={20}
                        />
                      ) : undefined
                    }
                    customIconPosition="right"
                    errors={
                      (errors.slug && touched.slug ? errors.slug : "") ||
                      (slugFetchSuccess && !fetchingSlug
                        ? "This URL is already taken."
                        : "")
                    }
                  />
                </div>
              )}
            </div>
          </form>
        </BgWrapper>

        {mode === "edit" && finalPayload.status !== "draft" && (
          <InfoCard
            leftIcon="info"
            title="Want to edit event visibility or who pays the fees?"
            text="Go to event settings to edit event visibility or who pays the fees"
            className="!bg-light-green md:!items-start"
            rightElement={
              <Icon
                svg="caret-down"
                width={24}
                height={24}
                className="-rotate-90"
              />
            }
            onClick={() => {
              const isDetailsEdited = !_.isEqual(currentPayload, finalPayload);

              if (isDetailsEdited) {
                handleModalOpen("confirmGoToEventSettings");
              } else {
                navigate(
                  `/events/${encodeURIComponent(currentPayload.slug ?? "")}/details?tab=settings`,
                  { replace: true },
                );
              }
            }}
          />
        )}
      </div>

      <div
        className={`flex w-full flex-col gap-[2px] ${mode === "edit" && finalPayload.status !== "draft" ? "!order-first" : ""}`}
      >
        <BgWrapper className="text-base font-bold text-grey-950">
          Event Image
        </BgWrapper>

        <BgWrapper className="flex flex-col items-center gap-4 md:flex-row">
          <EventImage
            image={values.image}
            className="!h-[300px] md:!h-[200px]"
          />

          <div className="order-first h-fit flex-1 text-sm md:order-last">
            {imageUploadError && (
              <ErrorText text={imageUploadError} className="!test-sm" />
            )}

            {!imageUploadError && (
              <p className="line-clamp-1 max-w-full text-ellipsis">
                {uploadedFile
                  ? uploadedFile.name
                  : "Choose file to upload as event image"}
              </p>
            )}

            <p className="mt-[3px] text-grey-500">
              Recommended size 2,000 x 2,000 px, Max file size{" "}
              {MAX_SIZE_MB / (1024 * 1024)}MB
            </p>

            <FileUploadButton
              type="button"
              variation="plain-grey"
              className="mt-4 !max-w-fit"
              onUpload={(uploaded) => {
                handleSingleFileUpload(uploaded, (url) => {
                  setFieldValue("image", url);
                });
              }}
              text={values.image ? "Replace Image" : "Upload Image"}
              icon="upload"
              disabled={uploading}
              loading={uploading}
            />
          </div>
        </BgWrapper>
      </div>

      <BgWrapper>
        <Button
          text={
            mode === "create" ||
            (mode === "edit" && finalPayload.status === "draft")
              ? "Next"
              : "Save Changes"
          }
          type="button"
          onClick={() => {
            handleSubmit();
          }}
          disabled={uploading || editingEvent || slugFetchSuccess}
          loading={editingEvent}
        />
      </BgWrapper>
    </>
  );
};
